import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle } from "lucide-react"
import { useState } from "react"

import { api } from "@/api/cliente"
import { comoErrorDeDinero } from "./errorDeDinero"
import { ModalCambioDivisa, type Propuesta } from "./ModalCambioDivisa"
import { type CuentaDePago, ModalPago, type Via } from "./ModalPago"

/**
 * El pago de un rubro, con sus datos, su escritura y su camino de excepción.
 *
 * Separado de `ModalPago` a propósito: el modal no sabe nada de la API y por eso se
 * puede probar montándolo con datos sueltos. Aquí viven las consultas y la mutación.
 *
 * ⚠ **Lo pendiente se calcula desde los pagos, no desde `estado_pago`.** `RubroOut`
 * dice si está `PARCIAL`, pero no cuánto queda: proponer el total sobre un rubro con
 * pagos previos es pedirle al servidor un `MONTO_EXCEDE_SALDO` que produjo el propio
 * formulario. Es la misma decisión que `ModalAbono` toma con el saldo de una CxP.
 *
 * ⚠ **La cifra de aquí es una propuesta, no una verdad.** El pendiente autoritativo
 * lo recalcula `liquidacion.py` desde el ledger, y el servidor rechaza el exceso
 * pase lo que pase. Esto sólo evita proponer una cifra que ya se sabe mala.
 *
 * ⚠ **Las vías que mueven banco van por `pagar-con-cambio`, no por `pagar`.** Esa
 * ruta mira el saldo antes de escribir y, si falta divisa, devuelve una propuesta de
 * conversión sin haber tocado nada. La vía CLIENTE sigue yendo por la ruta llana: no
 * mueve banco, así que no puede quedarse corta de divisa, y pedirle a la otra ruta
 * que mire un saldo sería pedirle que mire el de una cuenta que no interviene.
 */

type PagoDelRubro = {
  id: number
  monto: string
  anulado: boolean
}

type CuentaBancaria = {
  id: number
  nombre: string
  banco: string
  activo: boolean
}

/** `PropuestaCambioOut` tal como la entrega el servidor. */
type PropuestaDeLaApi = {
  cuenta_origen_id: number
  codigo_origen: string
  codigo_destino: string
  tasa: string
  monto_origen: string
  monto_destino: string
}

/** `PagoConCambioOut`: uno de los dos, nunca los dos vacíos. */
type RespuestaDePago = {
  pago: { id: number } | null
  propuesta: PropuestaDeLaApi | null
}

type DatosPago = {
  via: Via
  monto: string
  cuentaId: number | null
}

type Props = {
  expedienteId: number
  rubroId: number
  concepto: string
  /** Cantidad por precio, ya calculado por `totalDelRubro()`. */
  total: string
  moneda: "USD" | "BS"
  onCerrar: () => void
}

/** Lo que queda por pagar: el total menos los pagos que siguen vivos. */
export function pendienteDelRubro(total: string, pagos: PagoDelRubro[]): string {
  // ⚠ Sólo los no anulados. Un pago anulado no dejó de existir —nunca se borra— pero
  // tampoco cuenta: contarlo dejaría el rubro pareciendo pagado con dinero devuelto,
  // que es literalmente uno de los cuatro fallos del 2.0.
  const aplicado = pagos.filter((p) => !p.anulado).reduce((suma, p) => suma + Number(p.monto), 0)
  return (Number(total) - aplicado).toFixed(2)
}

/**
 * Si este código de divisa es uno que la interfaz sabe pintar.
 *
 * `codigo_origen` llega como texto libre del servidor. El día que haya una tercera
 * divisa, esto lo dice en vez de pintar importes con una moneda que
 * `formatearImporte` no conoce — y la conversión no se ofrece, que es lo correcto:
 * no se confirma lo que no se puede leer.
 */
function esMoneda(codigo: string): codigo is "USD" | "BS" {
  return codigo === "USD" || codigo === "BS"
}

export function DialogoPagoRubro({
  expedienteId,
  rubroId,
  concepto,
  total,
  moneda,
  onCerrar,
}: Props) {
  const clienteQuery = useQueryClient()
  // Lo que hay que repetir con `confirmado: true` si el operador acepta convertir.
  const [porConfirmar, setPorConfirmar] = useState<DatosPago | null>(null)
  const [propuesta, setPropuesta] = useState<Propuesta | null>(null)

  const pagos = useQuery({
    queryKey: ["pagos-rubro", rubroId],
    queryFn: () => api.get<PagoDelRubro[]>(`/api/v1/rubros/${rubroId}/pagos`),
  })

  const cuentas = useQuery({
    queryKey: ["cuentas-bancarias"],
    queryFn: () => api.get<CuentaBancaria[]>("/api/v1/cuentas-bancarias"),
  })

  const pagar = useMutation({
    mutationFn: async (v: { datos: DatosPago; confirmado: boolean }): Promise<RespuestaDePago> => {
      if (v.datos.cuentaId === null) {
        const pago = await api.post<{ id: number }>(`/api/v1/rubros/${rubroId}/pagar`, {
          via: v.datos.via,
          monto: v.datos.monto,
          cuenta_id: null,
        })
        return { pago, propuesta: null }
      }
      return api.post<RespuestaDePago>(`/api/v1/rubros/${rubroId}/pagar-con-cambio`, {
        via: v.datos.via,
        monto: v.datos.monto,
        cuenta_id: v.datos.cuentaId,
        confirmado: v.confirmado,
      })
    },
    onSuccess: (respuesta, variables) => {
      // Sin pago y con propuesta: el servidor NO escribió nada y está esperando que
      // una persona decida. Se enseña de dónde sale, cuánto y a qué tasa.
      if (respuesta.pago === null) {
        const p = respuesta.propuesta
        if (p !== null && esMoneda(p.codigo_origen) && esMoneda(p.codigo_destino)) {
          setPropuesta({
            cuentaOrigen:
              (cuentas.data ?? []).find((c) => c.id === p.cuenta_origen_id)?.nombre ??
              `cuenta ${p.cuenta_origen_id}`,
            codigoOrigen: p.codigo_origen,
            codigoDestino: p.codigo_destino,
            tasa: p.tasa,
            montoOrigen: p.monto_origen,
            montoDestino: p.monto_destino,
          })
          setPorConfirmar(variables.datos)
        }
        return
      }

      // El pago toca cuatro sitios y los cuatro se invalidan. La vía FINANCIADO
      // genera una CxC al cliente y las otras dos mueven banco: dejar la bandeja de
      // cobrar o el saldo de tesorería sin refrescar enseña cifras viejas junto a
      // una operación que el operador acaba de hacer, y eso es lo que hace que
      // alguien la repita.
      clienteQuery.invalidateQueries({ queryKey: ["rubros-expediente", expedienteId] })
      clienteQuery.invalidateQueries({ queryKey: ["pagos-rubro", rubroId] })
      clienteQuery.invalidateQueries({ queryKey: ["cxc"] })
      clienteQuery.invalidateQueries({ queryKey: ["saldo-cuenta"] })
      onCerrar()
    },
  })

  if (pagos.isPending || cuentas.isPending) {
    return (
      <p role="status" className="p-4 text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando el rubro…
      </p>
    )
  }

  if (pagos.isError) {
    // Sin los pagos no se puede proponer un pendiente, y proponer el total sería
    // proponer una cifra que puede estar mal. Antes no ofrecer nada.
    return (
      <p
        role="alert"
        className="flex items-center gap-2 p-4 text-sm"
        style={{ color: "var(--destructive)" }}
      >
        <AlertCircle size={16} aria-hidden={true} />
        No se pudieron leer los pagos de este rubro.
      </p>
    )
  }

  // La propuesta REEMPLAZA al formulario mientras está viva. Enseñar los dos a la vez
  // deja dos botones que escriben —«Pagar» y «Confirmar conversión»— sobre la misma
  // operación, y el que no toca es el que parece obvio.
  if (propuesta !== null && porConfirmar !== null) {
    return (
      <ModalCambioDivisa
        propuesta={propuesta}
        onConfirmar={() => pagar.mutate({ datos: porConfirmar, confirmado: true })}
        onCerrar={() => {
          // Volver al formulario, no cerrarlo todo: quien no quiere convertir todavía
          // puede pagar menos, o por otra vía.
          setPropuesta(null)
          setPorConfirmar(null)
        }}
      />
    )
  }

  return (
    <ModalPago
      concepto={concepto}
      pendiente={pendienteDelRubro(total, pagos.data)}
      moneda={moneda}
      // Una cuenta inactiva sigue existiendo para el histórico, pero no se le puede
      // sacar dinero hoy. Ofrecerla es ofrecer un 409.
      cuentas={(cuentas.data ?? [])
        .filter((c) => c.activo)
        .map<CuentaDePago>((c) => ({
          id: c.id,
          nombre: c.nombre,
          banco: c.banco,
        }))}
      error={comoErrorDeDinero(pagar.error)}
      onPagar={(datos) => pagar.mutate({ datos, confirmado: false })}
      onCerrar={onCerrar}
    />
  )
}
